import { useState, useRef, useEffect } from "react";
import { GoogleGenAI, HarmBlockThreshold, HarmCategory } from "@google/genai";
import { icons } from "../assets/icons";
import botAvatar from "../assets/alnickAtKayak.webp";

const Chatbot = () => {
  const messagesEndRef = useRef(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Hi! 👋 Thank you for visiting my portfolio website and I’m glad you’re here. Got questions about coding, building websites, or my tech experiences? Let’s chat!",
    },
  ]);
  const [input, setInput] = useState("");

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { sender: "user", text: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    try {
      const genAI = new GoogleGenAI({
        apiKey:
          process.env.REACT_APP_GEMINI_API_KEY ||
          "AIzaSyBxrv1eFyyD9Wc6Sz4M2UsMEjaOYbq5cyY",
      });

      const config = {
        safetySettings: [
          {
            category: HarmCategory.HARM_CATEGORY_HARASSMENT,
            threshold: HarmBlockThreshold.BLOCK_LOW_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
            threshold: HarmBlockThreshold.BLOCK_LOW_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
        ],
        responseMimeType: "text/plain",
        systemInstruction: [
          {
            text: `You are Alnick Clores, a real person, not an AI. You speak in the first person, respond informally and conversationally, and do not refer to yourself as an AI or language model.

            You specialize in frontend development and are skilled in JavaScript, TypeScript, ReactJS, NextJS, TailwindCSS, and building REST APIs. You also have experience on the backend using Node.js and Express, and work with databases like MySQL, Supabase, and Firebase.

            You also have some mobile development background using Kotlin for Android apps.

            You're friendly, humorous, and professional in tone — often using emojis to make conversations more relatable (but never unprofessional). You explain technical concepts clearly and are happy to share career insights, coding tips, and project advice 😄.

            You avoid answering personal questions related to family, romantic relationships, or political topics — and redirect such questions back to professional or tech-related discussions.

            💼 Projects You’ve Built
            Here are some projects you’ve worked on and are proud of:

            OJT Folio – A SaaS web app that helps students track OJT hours with file uploads, daily time logs, and automated weekly reports.

            MAR: Menu Augmented Resto – A web app with an interactive 3D menu. Built to make ordering systems more efficient for restaurants.

            SafeVenture – An Android app that monitors unauthorized login attempts, sends image alerts, and shares access logs with friends.

            Keyed Vigenere Cipher – A simple web app that encrypts and decrypts messages using a custom cipher key`,
          },
        ],
      };

      const model = "gemini-1.5-flash-8b";
      const contents = [
        {
          role: "user",
          parts: [{ text: input }],
        },
      ];

      const stream = await genAI.models.generateContentStream({
        model,
        config,
        contents,
      });

      let reply = "";
      setMessages((prev) => [...prev, { sender: "bot", text: "..." }]);

      for await (const chunk of stream) {
        reply += chunk.text || "";
        setMessages((prev) =>
          prev.map((msg, idx) =>
            idx === prev.length - 1 ? { ...msg, text: reply } : msg
          )
        );
      }
    } catch (error) {
      console.error("Streaming error:", error);
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "Oops 😅 I hit a snag. Try again in a bit!",
        },
      ]);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <>
      <div
        onClick={() => setChatOpen(!chatOpen)}
        className="flex items-center gap-1 fixed right-3 sm:right-5 bottom-3 sm:bottom-5 bg-black dark:bg-white text-white dark:text-black px-3 sm:px-5 py-2 sm:py-3 rounded-full cursor-pointer z-50 shadow-lg hover:shadow-xl transition-all duration-200"
      >
        <span className="scale-75 sm:scale-100 animate-gentle-bounce">
          {icons.chat}
        </span>
        <span className="text-xs sm:text-base font-semibold whitespace-nowrap">
          Chat with Alnick
        </span>
      </div>

      {chatOpen && (
        <div className="fixed bottom-16 sm:bottom-24 right-3 sm:right-5 w-[85vw] sm:w-96 h-[60vh] sm:h-[50vh] bg-black dark:bg-white text-white dark:text-black rounded-lg shadow-lg flex flex-col z-40 overflow-hidden border border-gray-500 dark:border-gray-700">
          {/* Header */}
          <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-black text-black dark:text-white">
            <div className="flex items-center gap-2 sm:gap-3">
              <img
                src={botAvatar}
                alt="Bot"
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full"
              />
              <div>
                <p className="text-xs sm:text-sm font-bold">Chat with Alnick</p>
                <p className="text-[10px] sm:text-xs">
                  <span className="text-green-500">●</span> Online · Powered by
                  Google Gemini
                </p>
              </div>
            </div>
            <button
              onClick={() => setChatOpen(false)}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl transition-colors"
            >
              &times;
            </button>
          </div>

          {/* Chat Messages */}
          <div
            className="flex-1 overflow-y-auto px-4 pt-3 pb-6 space-y-3 bg-white dark:bg-black scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "#4B5563 transparent",
            }}
          >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex items-start ${
                  msg.sender === "user" ? "justify-end" : "justify-start"
                }`}
              >
                {msg.sender === "bot" ? (
                  <div className="flex flex-col items-start max-w-[80%] space-y-1">
                    <div className="flex items-center gap-2">
                      <img
                        src={botAvatar}
                        alt="Bot"
                        className="w-6 h-6 rounded-full"
                      />
                      <h1 className="text-sm font-semibold dark:text-white text-black">
                        Alnick Clores
                      </h1>
                    </div>
                    <div className="bg-gray-200 dark:bg-gray-800 text-black dark:text-white px-4 py-2 rounded-lg text-sm">
                      {msg.text}
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-end">
                    <div className="bg-blue-600 text-white px-4 py-2 rounded-lg max-w-[80%] text-sm break-words whitespace-normal">
                      {msg.text}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Field */}
          <div className="flex items-center gap-2 px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-black">
            <input
              type="text"
              className="flex-1 bg-transparent text-sm text-black dark:text-white placeholder-gray-400 border border-gray-200 dark:border-gray-700 p-2 rounded-lg focus:outline-none"
              placeholder="Type a message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
            />
            <button
              onClick={handleSend}
              className="text-gray-500 hover:text-gray-200 transition duration-200"
            >
              {icons.send}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
